import { useCallback, useEffect, useState } from 'react';
import { batchId, maakTerugbetalingsbatch } from '@qc/core';
import { logAudit } from '../audit.js';
import { supabase } from '../supabase.js';

/**
 * Terugbetalingen aan klanten die per overschrijving gaan (docs/open-punten.md,
 * punt 8). De kassa legt ze vast; hier maakt de admin er één SEPA-bestand van
 * en markeert ze als uitbetaald. Regel 13: binnen één werkdag.
 */

export interface TerugbetalingRij {
  id: string;
  naam: string;
  iban: string;
  bedragCent: number;
  reden: string;
  aangemaaktOp: string;
  uitbetaaldOp: string | null;
  batchId: string | null;
}

export interface BatchUitkomst {
  ok: boolean;
  melding: string;
}

export interface Terugbetalingenbeeld {
  laden: boolean;
  fout: string | null;
  open: TerugbetalingRij[];
  uitbetaald: TerugbetalingRij[];
  ververs: () => Promise<void>;
  /** Maakt het SEPA-bestand, start de download en markeert de rijen als uitbetaald. */
  maakBatch: () => Promise<BatchUitkomst>;
}

function eerstvolgendeWerkdag(vanaf: Date): string {
  const datum = new Date(vanaf);
  datum.setUTCDate(datum.getUTCDate() + 1);
  while (datum.getUTCDay() === 0 || datum.getUTCDay() === 6) {
    datum.setUTCDate(datum.getUTCDate() + 1);
  }
  return datum.toISOString().slice(0, 10);
}

function downloadBestand(naam: string, inhoud: string): void {
  const url = URL.createObjectURL(new Blob([inhoud], { type: 'application/xml' }));
  const schakel = document.createElement('a');
  schakel.href = url;
  schakel.download = naam;
  schakel.click();
  URL.revokeObjectURL(url);
}

export function useTerugbetalingen(): Terugbetalingenbeeld {
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState<string | null>(null);
  const [rijen, setRijen] = useState<TerugbetalingRij[]>([]);

  const ververs = useCallback(async () => {
    const { data, error } = await supabase
      .from('retouren')
      .select('id, bedrag_cent, reden, aangemaakt_op, uitbetaald_op, batch_id, iban, naam_rekeninghouder')
      .eq('wijze', 'overschrijving')
      .order('aangemaakt_op', { ascending: false })
      .limit(200);
    if (error) {
      setFout('De terugbetalingen konden niet worden opgehaald.');
      setLaden(false);
      return;
    }
    setRijen(
      (data ?? []).map((rij) => ({
        id: rij.id,
        naam: rij.naam_rekeninghouder ?? '',
        iban: rij.iban ?? '',
        bedragCent: Number(rij.bedrag_cent),
        reden: rij.reden,
        aangemaaktOp: rij.aangemaakt_op,
        uitbetaaldOp: rij.uitbetaald_op,
        batchId: rij.batch_id,
      })),
    );
    setFout(null);
    setLaden(false);
  }, []);

  useEffect(() => {
    void (async () => {
      await ververs();
    })();
  }, [ververs]);

  const open = rijen.filter((rij) => rij.uitbetaaldOp === null);
  const uitbetaald = rijen.filter((rij) => rij.uitbetaaldOp !== null);

  const maakBatch = useCallback(async (): Promise<BatchUitkomst> => {
    if (open.length === 0) return { ok: false, melding: 'Er staat niets open.' };

    const { data: onze } = await supabase.from('bedrijfsgegevens').select('naam, iban').maybeSingle();
    if (onze === null || onze.iban === null || onze.iban.trim() === '') {
      return {
        ok: false,
        melding: 'Vul eerst het IBAN van Quality Cashless in bij Bedrijfsgegevens.',
      };
    }

    const nu = new Date();
    const bestand = maakTerugbetalingsbatch({
      nu,
      uitvoerdatum: eerstvolgendeWerkdag(nu),
      opdrachtgeverNaam: onze.naam,
      opdrachtgeverIban: onze.iban,
      terugbetalingen: open.map((rij) => ({
        id: rij.id,
        naam: rij.naam,
        iban: rij.iban,
        bedragCent: rij.bedragCent,
        omschrijving: rij.reden,
      })),
    });
    if (bestand.aantalOpdrachten === 0) {
      return {
        ok: false,
        melding: `Geen enkele terugbetaling kon in het bestand: ${bestand.overgeslagen
          .map((o) => `${o.naam} (${o.reden})`)
          .join(', ')}.`,
      };
    }

    downloadBestand(bestand.bestandsnaam, bestand.xml);

    // Alleen de rijen die écht in het bestand zitten markeren; wat de
    // SEPA-laag oversloeg blijft open staan en staat in de melding.
    const overgeslagenNamen = new Set(bestand.overgeslagen.map((o) => o.naam));
    const ids = open.filter((rij) => !overgeslagenNamen.has(rij.naam)).map((rij) => rij.id);
    const stempel = nu.toISOString();
    const kenmerk = batchId(nu);
    const { error } = await supabase
      .from('retouren')
      .update({ uitbetaald_op: stempel, batch_id: kenmerk, status: 'geslaagd', afgerond_op: stempel })
      .in('id', ids);
    if (error) {
      return {
        ok: false,
        melding:
          'Het bestand is gedownload, maar de terugbetalingen konden niet als uitbetaald gemarkeerd worden. Probeer het opnieuw vóór je het bestand bij de bank uploadt.',
      };
    }
    void logAudit('terugbetalingsbatch_gemaakt', 'retouren', kenmerk, {
      aantal: ids.length,
      totaalCent: bestand.totaalCent,
    });
    await ververs();

    const rest =
      bestand.overgeslagen.length === 0
        ? ''
        : ` Niet meegenomen: ${bestand.overgeslagen.map((o) => `${o.naam} (${o.reden})`).join(', ')}.`;
    return {
      ok: true,
      melding: `${bestand.bestandsnaam} gedownload: ${bestand.aantalOpdrachten} terugbetaling(en). Upload het bij Rabobank.${rest}`,
    };
  }, [open, ververs]);

  return { laden, fout, open, uitbetaald, ververs, maakBatch };
}
